/**
 * Which session a request speaks for.
 *
 * Two sessions can be live in the same browser at once, and they are not the
 * same account: the staff session (a ChessDirector account, `token` in local
 * storage, what /manage and the admin APIs want) and the player session (a
 * club member, `paircraft_player_auth`). They are stored apart, but the one
 * shared axios client used to blur them — it sent whichever token it found,
 * so an admin call made without a staff session went out holding a member's
 * credential, and a 401 on any call signed the *staff* session out and
 * bounced the tab to /sign-in.
 *
 * So every request records which session it used, and the failure handling
 * acts only on that one. Kept free of axios so the rules are testable.
 */

export type AuthSource = 'staff' | 'player' | 'explicit' | 'none';

/**
 * Endpoints only a ChessDirector account may call. A member's token here is
 * never right — it would be rejected anyway, and sending it just turns "you
 * are not signed in as staff" into a confusing 403.
 */
const STAFF_ONLY_PREFIXES = ['/admin'];

/** True when `url` addresses a staff-only endpoint. */
export function isStaffOnlyPath(url?: string | null): boolean {
  if (!url) return false;
  // Both '/admin/users' and a full 'https://host/api/admin/users' reach here.
  const path = url.replace(/^https?:\/\/[^/]+/, '');
  const afterApi = path.replace(/^\/api(?=\/|$)/, '');
  return STAFF_ONLY_PREFIXES.some((p) => afterApi === p || afterApi.startsWith(`${p}/`));
}

export interface TokenChoice {
  token: string | null;
  source: AuthSource;
}

/**
 * The credential for a request: the staff token when there is one, the
 * player's otherwise — except on staff-only endpoints, which take the staff
 * token or nothing at all.
 */
export function pickAuthToken(
  url: string | undefined,
  tokens: { staffToken?: string | null; playerToken?: string | null }
): TokenChoice {
  const staff = tokens.staffToken || null;
  const player = tokens.playerToken || null;

  if (staff) return { token: staff, source: 'staff' };
  if (isStaffOnlyPath(url)) return { token: null, source: 'none' };
  if (player) return { token: player, source: 'player' };
  return { token: null, source: 'none' };
}

/**
 * A 401 clears the session that was actually used, and only that one: a
 * member's expired token must never sign a director out, or the other way
 * about.
 */
export function shouldClearStaffSession(source: AuthSource): boolean {
  return source === 'staff';
}

/**
 * Whether to throw the tab at the platform sign-in page after clearing.
 *
 * Not on a page that offers sign-in in place — /manage renders the sign-in
 * itself the moment the session goes, so redirecting would take a director
 * off the club's site for no reason.
 */
export function shouldRedirectToSignIn(source: AuthSource, pathname: string): boolean {
  if (source !== 'staff') return false;
  const path = pathname || '/';
  return !path.startsWith('/sign-in') && !path.startsWith('/manage');
}
